import React, {useState} from 'react'
import './todos.less'
import {CheckOutlined, QuestionCircleOutlined} from '@ant-design/icons'
import {instance as toDoService} from '../../services/ToDoService'
import {useIntl} from 'react-intl'
import {DatePicker, message, Popconfirm, Button} from 'antd'
import {ToDoTypeFormSelector} from './ToDoTypeFormSelector'
import dayjs from 'dayjs'
import {
  CustomJournalEventType,
  CustomType,
  DefaultEventType,
  EventTypeMetadata,
  JournalEventType,
  PredefinedEventTypeId,
  ToDoEventTypesMatching,
  ToDoTypeOption,
} from '../../entities/journalRecord'
import {ToDo} from '../../entities/todoList'
import {instance as journalService} from '../../services/JournalService'
import {useRevalidator, useSubmit} from 'react-router-dom'
import {markPlantForRevalidation} from '../../routes/plantRevalidator'
import {markToDoForRevalidation} from '../../routes/todoRevalidator'

export type FinishToDoButtonProps = {
  userJournalEventTypes: CustomJournalEventType[]
  finishedTodo: ToDo
  isText: boolean
}

const defaultEventType: ToDoTypeOption = {
  id: null,
  name: EventTypeMetadata.general.name,
  icon: EventTypeMetadata.general.icon,
}

export function FinishToDoButton(props: FinishToDoButtonProps) {
  const intl = useIntl()

  const [completionDate, setCompletionDate] = useState(new Date())
  const [finishedToDoEventType, setFinishedToDoEventType] = useState<ToDoTypeOption | null>(
    !props.finishedTodo.type ||
      props.finishedTodo.type.type === 'custom' ||
      !props.finishedTodo.type?.type
      ? defaultEventType
      : {
          id: null,
          name: EventTypeMetadata[ToDoEventTypesMatching[props.finishedTodo.type.type]].name,
          icon: EventTypeMetadata[ToDoEventTypesMatching[props.finishedTodo.type.type]].icon,
        }
  )
  const submit = useSubmit()

  const revalidator = useRevalidator()

  const onCreateNewUserEventType = async (name: string, icon: string) => {
    const result = await journalService.createUserJournalEventType(name, icon)
    if (result.success) {
      markPlantForRevalidation()
      markToDoForRevalidation()
      revalidator.revalidate()
    }
    return result
  }

  const onEditUserEventType = async (editedType: CustomType) => {
    const editingResult = await journalService.editUserJournalEventType(editedType)
    if (editingResult.success) {
      markPlantForRevalidation()
      markToDoForRevalidation()
      revalidator.revalidate()
    }
    return editingResult
  }

  const onDeleteUserEventType = async (deletedTypeId: number) => {
    const deletingResult = await journalService.deleteUserJournalEventType(deletedTypeId)

    if (deletingResult.success) {
      markPlantForRevalidation()
      markToDoForRevalidation()
      revalidator.revalidate()
    }
  }

  return (
    <Popconfirm
      placement="bottom"
      title={intl.formatMessage({id: 'areYouSureYouWantToFinish'})}
      description={
        <div>
          <div>{intl.formatMessage({id: 'thisOperationWillMoveTask'})}</div>
          <div>
            {intl.formatMessage({id: 'selectTheTaskCompletionDate'})}:
            <div>
              <DatePicker
                defaultValue={dayjs(completionDate)}
                onChange={(value) => {
                  setCompletionDate(value.toDate())
                }}
              />
              <ToDoTypeFormSelector
                chosenType={finishedToDoEventType || undefined}
                onChange={setFinishedToDoEventType}
                commonTypes={(Object.keys(EventTypeMetadata) as PredefinedEventTypeId[]).map(
                  (type) => ({
                    id: null,
                    name: type,
                    icon: EventTypeMetadata[type].icon,
                  })
                )}
                userTypes={props.userJournalEventTypes.map((type) => ({
                  id: type.customJournalEventType.id,
                  name: type.customJournalEventType.name,
                  icon: type.customJournalEventType.icon,
                }))}
                createNewItem={onCreateNewUserEventType}
                editType={onEditUserEventType}
                deleteUserType={onDeleteUserEventType}
                defaultWhenCleared={defaultEventType}
                isAllowClear={false}
              />
            </div>
          </div>
        </div>
      }
      icon={<QuestionCircleOutlined style={{color: 'red'}} />}
      onConfirm={async () => {
        const result = await toDoService.finishToDo(
          props.finishedTodo,
          completionDate,
          finishedToDoEventType || defaultEventType
        )
        if (result.success) {
          submit(null, {method: 'post'})
        } else {
          message.open({
            type: 'error',
            content: result.error?.message,
          })
        }
      }}
      okText={intl.formatMessage({id: 'yes'})}
      cancelText={intl.formatMessage({id: 'no'})}
    >
      {props.isText ? (
        <Button type="primary" style={{display: 'flex'}}>
          <CheckOutlined key="done" />
          {props.isText && <span>{intl.formatMessage({id: 'finish'})}</span>}
        </Button>
      ) : (
        <CheckOutlined
          key="done"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        />
      )}
    </Popconfirm>
  )
}
